import type { DrawStage } from '@/draw/types';
import { isMatchDone, type DrawLadders, type Ladder } from './ladderModel';

/** One block on the page: a round-robin pool, or a knockout (a pool played as a knockout, or a ladder). */
export type DrawSection = { kind: 'pool'; stage: DrawStage } | { kind: 'ladder'; ladder: Ladder };

/**
 * Layer 1 of the draw navigation. The pools group holds every pool of the draw — round-robin and
 * knockout qualifying pools alike — stacked on one page; every other ladder (Main Draw, Plate, …)
 * is a group of its own with its playoffs nested inside it.
 */
export interface DrawGroup {
  key: string;
  pools: boolean;
  sections: DrawSection[];
}

export const stageOf = (s: DrawSection) => (s.kind === 'pool' ? s.stage : s.ladder.stage);
export const sectionId = (s: DrawSection) => stageOf(s).stageId;

export function buildDrawGroups(model: DrawLadders): DrawGroup[] {
  const isPoolLadder = (l: Ladder) => l.stage.context === 'qualifying';
  const poolSections: DrawSection[] = [
    ...model.pools.map((stage) => ({ kind: 'pool' as const, stage })),
    ...model.ladders.filter(isPoolLadder).map((ladder) => ({ kind: 'ladder' as const, ladder })),
  ].sort((a, b) => (stageOf(a).order ?? 0) - (stageOf(b).order ?? 0));

  const groups: DrawGroup[] = poolSections.length > 0 ? [{ key: 'pools', pools: true, sections: poolSections }] : [];
  for (const ladder of model.ladders) {
    if (!isPoolLadder(ladder)) groups.push({ key: `ladder:${ladder.stage.stageId}`, pools: false, sections: [{ kind: 'ladder', ladder }] });
  }
  return groups;
}

const sectionOpen = (s: DrawSection) =>
  s.kind === 'pool'
    ? s.stage.rounds.some((r) => r.matches.some((m) => !isMatchDone(m)))
    : !s.ladder.finished || s.ladder.rounds.some((r) => r.feeders.some((f) => !f.finished));

function defaultGroup(groups: DrawGroup[]): string | null {
  const pools = groups.find((g) => g.pools);
  const others = groups.filter((g) => !g.pools);
  if (pools && (others.length === 0 || pools.sections.some(sectionOpen))) return pools.key;
  return others[0]?.key ?? null;
}

/** Keep the viewer's group across live rebuilds while it still exists; otherwise the default. */
export function resolveGroup(choice: string | null, groups: DrawGroup[]): string | null {
  if (choice && groups.some((g) => g.key === choice)) return choice;
  return defaultGroup(groups);
}
